import * as React from "react";
import useSWR from "swr";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  FormControlLabel,
  Switch,
  Box,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
} from "@mui/material";
import { axiosInstance } from "src/api/axiosInstance";
import type { BrandItem, BrandListItem } from "src/api/brand/brandApi";

export type BrandUpsertPayload = {
  brandCode?: string;
  brandCategoryId?: number;

  brandNameKo: string;
  brandNameEn?: string;
  brandDescription?: string;

  officialWebsite?: string;
  originCountry?: string;

  displayOrder?: number;
  isFeatured?: boolean;
  isActive?: boolean;

  logoFile?: File | null;
  thumbnailFile?: File | null;
};

type Props = {
  open: boolean;
  item: BrandItem | null;
  listItem: BrandListItem | null;
  loading?: boolean;
  onClose: () => void;
  onSubmit: (payload: BrandUpsertPayload) => Promise<void>;
};

type BrandCategory = {
  id: number;
  categoryCode: string;
  categoryName: string;
};

type CategoryListResponse = {
  statusCode: number; // ✅ 성공 1
  message: string;
  data: BrandCategory[];
};

const previewBoxStyle = {
  width: 160,
  height: 160,
  borderRadius: 2,
  border: "1px solid",
  borderColor: "divider",
  overflow: "hidden",
  bgcolor: "background.default",
};

async function getBrandCategoryListApi(): Promise<CategoryListResponse> {
  const res = await axiosInstance.get<CategoryListResponse>(
    "/v1/admin/baseinfo/brand/category/list"
  );
  return res.data;
}

export default function BrandUpsertDialog({
  open,
  item,
  listItem,
  loading = false,
  onClose,
  onSubmit,
}: Props) {
  const src: any = item ?? listItem ?? {};

  // ✅ category list
  const {
    data: catRes,
    error: catErr,
    isLoading: catLoading,
  } = useSWR(open ? ["brand/category/list"] : null, getBrandCategoryListApi, {
    revalidateOnFocus: false,
  });

  const categories = React.useMemo<BrandCategory[]>(() => {
    if (!catRes) return [];
    if (catRes.statusCode !== 1) return [];
    return Array.isArray(catRes.data) ? catRes.data : [];
  }, [catRes]);

  const [brandNameKo, setBrandNameKo] = React.useState("");
  const [brandNameEn, setBrandNameEn] = React.useState("");
  const [brandDescription, setBrandDescription] = React.useState("");
  const [officialWebsite, setOfficialWebsite] = React.useState("");
  const [originCountry, setOriginCountry] = React.useState("");
  const [displayOrder, setDisplayOrder] = React.useState(0);
  const [isFeatured, setIsFeatured] = React.useState(false);
  const [isActive, setIsActive] = React.useState(true);

  const [brandCode, setBrandCode] = React.useState("");
  const [brandCategoryId, setBrandCategoryId] = React.useState<number | "">("");

  const [logoFile, setLogoFile] = React.useState<File | null>(null);
  const [thumbnailFile, setThumbnailFile] = React.useState<File | null>(null);

  // ✅ objectURL 관리
  const [logoObjectUrl, setLogoObjectUrl] = React.useState<string | null>(null);
  const [thumbObjectUrl, setThumbObjectUrl] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;

    setBrandNameKo(src.brandNameKo ?? "");
    setBrandNameEn(src.brandNameEn ?? "");
    setBrandDescription(src.brandDescription ?? "");
    setOfficialWebsite(src.officialWebsite ?? "");
    setOriginCountry(src.originCountry ?? "");
    setDisplayOrder(typeof src.displayOrder === "number" ? src.displayOrder : 0);
    setIsFeatured(!!src.isFeatured);
    setIsActive(src.isActive ?? true);

    setBrandCode(src.brandCode ?? "");
    setBrandCategoryId(src.brandCategoryId != null ? Number(src.brandCategoryId) : "");

    setLogoFile(null);
    setThumbnailFile(null);
  }, [open, item, listItem]);

  React.useEffect(() => {
    if (logoObjectUrl) URL.revokeObjectURL(logoObjectUrl);
    if (!logoFile) {
      setLogoObjectUrl(null);
      return;
    }
    const url = URL.createObjectURL(logoFile);
    setLogoObjectUrl(url);
    return () => URL.revokeObjectURL(url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logoFile]);

  React.useEffect(() => {
    if (thumbObjectUrl) URL.revokeObjectURL(thumbObjectUrl);
    if (!thumbnailFile) {
      setThumbObjectUrl(null);
      return;
    }
    const url = URL.createObjectURL(thumbnailFile);
    setThumbObjectUrl(url);
    return () => URL.revokeObjectURL(url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thumbnailFile]);

  const logoUrl: string | undefined = logoObjectUrl ?? src.logoFiles?.[0]?.fileUrl ?? undefined;

  const thumbUrl: string | undefined =
    thumbObjectUrl ?? src.thumbnailFiles?.[0]?.fileUrl ?? undefined;

  const disabled = loading || !brandNameKo.trim();

  const categoryErrorMsg = catErr ? ((catErr as any)?.message ?? "카테고리 로드 실패") : null;

  const categoryApiErrorMsg =
    catRes && catRes.statusCode !== 1 ? catRes.message || "카테고리 로드 실패" : null;

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontWeight: 900 }}>{item ? "브랜드 수정" : "브랜드 등록"}</DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ pt: 1 }}>
          {/* 카테고리 로드 상태 */}
          {categoryErrorMsg ? <Alert severity="error">{categoryErrorMsg}</Alert> : null}
          {categoryApiErrorMsg ? <Alert severity="error">{categoryApiErrorMsg}</Alert> : null}

          {/* 기본 정보 */}
          <Stack direction="row" spacing={2}>
            <TextField
              label="브랜드명 (KO) *"
              value={brandNameKo}
              onChange={(e) => setBrandNameKo(e.target.value)}
              disabled={loading}
              fullWidth
              size="small"
            />
            <TextField
              label="브랜드명 (EN)"
              value={brandNameEn}
              onChange={(e) => setBrandNameEn(e.target.value)}
              disabled={loading}
              fullWidth
              size="small"
            />
          </Stack>

          {/* 코드/카테고리 */}
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              label="브랜드 코드"
              value={brandCode}
              onChange={(e) => setBrandCode(e.target.value)}
              disabled={loading}
              size="small"
              sx={{ width: 220 }}
            />

            <FormControl size="small" sx={{ minWidth: 260 }} disabled={loading || catLoading}>
              <InputLabel id="brand-category-label">브랜드 카테고리</InputLabel>
              <Select
                labelId="brand-category-label"
                label="브랜드 카테고리"
                value={brandCategoryId}
                onChange={(e) =>
                  setBrandCategoryId(e.target.value === "" ? "" : Number(e.target.value))
                }
                renderValue={(val) => {
                  if (!val) return "선택";
                  const found = categories.find((c) => c.id === val);
                  return found ? `${found.categoryName} (${found.categoryCode})` : String(val);
                }}
              >
                <MenuItem value="">선택</MenuItem>
                {categories.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.categoryName} ({c.categoryCode})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {catLoading ? <CircularProgress size={18} /> : null}
          </Stack>

          {/* 설명 */}
          <TextField
            label="브랜드 설명"
            value={brandDescription}
            onChange={(e) => setBrandDescription(e.target.value)}
            disabled={loading}
            multiline
            minRows={3}
            fullWidth
            size="small"
          />

          {/* 웹사이트/원산지 */}
          <Stack direction="row" spacing={2}>
            <TextField
              label="공식 웹사이트"
              value={officialWebsite}
              onChange={(e) => setOfficialWebsite(e.target.value)}
              disabled={loading}
              fullWidth
              size="small"
            />
            <TextField
              label="원산지"
              value={originCountry}
              onChange={(e) => setOriginCountry(e.target.value)}
              disabled={loading}
              fullWidth
              size="small"
            />
          </Stack>

          {/* 노출/토글 */}
          <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
            <TextField
              label="노출 순서"
              type="number"
              value={displayOrder}
              onChange={(e) => setDisplayOrder(Number(e.target.value))}
              disabled={loading}
              size="small"
              sx={{ width: 160 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={isFeatured}
                  onChange={(e) => setIsFeatured(e.target.checked)}
                  disabled={loading}
                />
              }
              label="추천"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  disabled={loading}
                />
              }
              label="활성"
            />
          </Stack>

          <Divider />

          {/* 로고 업로드 */}
          <Stack direction="row" spacing={3} alignItems="center">
            <Box sx={previewBoxStyle}>
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt="logo"
                  style={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
              ) : (
                <Stack alignItems="center" justifyContent="center" sx={{ height: "100%" }}>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    로고 없음
                  </Typography>
                </Stack>
              )}
            </Box>

            <Stack spacing={1} sx={{ flex: 1 }}>
              <Typography fontWeight={800}>로고 이미지</Typography>
              <input
                type="file"
                accept="image/*"
                disabled={loading}
                onChange={(e) => setLogoFile(e.target.files?.[0] ?? null)}
              />
              {logoFile ? (
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  선택됨: {logoFile.name}
                </Typography>
              ) : null}
            </Stack>
          </Stack>

          {/* 썸네일 업로드 */}
          <Stack direction="row" spacing={3} alignItems="center">
            <Box sx={{ ...previewBoxStyle, width: 220, height: 140 }}>
              {thumbUrl ? (
                <img
                  src={thumbUrl}
                  alt="thumbnail"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <Stack alignItems="center" justifyContent="center" sx={{ height: "100%" }}>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    썸네일 없음
                  </Typography>
                </Stack>
              )}
            </Box>

            <Stack spacing={1} sx={{ flex: 1 }}>
              <Typography fontWeight={800}>썸네일 이미지</Typography>
              <input
                type="file"
                accept="image/*"
                disabled={loading}
                onChange={(e) => setThumbnailFile(e.target.files?.[0] ?? null)}
              />
              {thumbnailFile ? (
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  선택됨: {thumbnailFile.name}
                </Typography>
              ) : null}
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading} variant="outlined">
          취소
        </Button>
        <Button
          variant="contained"
          disabled={disabled}
          onClick={async () => {
            await onSubmit({
              brandNameKo: brandNameKo.trim(),
              brandNameEn: brandNameEn.trim() || undefined,
              brandDescription: brandDescription.trim() || undefined,
              officialWebsite: officialWebsite.trim() || undefined,
              originCountry: originCountry.trim() || undefined,
              displayOrder: Number.isFinite(displayOrder) ? displayOrder : 0,
              isFeatured,
              isActive,
              brandCode: brandCode.trim() || undefined,
              brandCategoryId: brandCategoryId === "" ? undefined : Number(brandCategoryId),
              logoFile,
              thumbnailFile,
            });
          }}
        >
          {loading ? "저장 중..." : "저장"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
